import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import GitHubIcon from '@mui/icons-material/GitHub';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import Logo from './Logo';
import Leadership, { LEADERSHIP_ID } from './Leadership';
import { line } from '../theme';

const NAV_IDS = ['services', 'clients', 'about', 'contact'];

const linkSx = { color: 'text.secondary', '&:hover': { color: 'text.primary' } };

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [leadershipOpen, setLeadershipOpen] = React.useState(false);

  // Footer is now shared with /nizro, which has none of these sections — so
  // a click there has to navigate home with the section as a hash, same as
  // the equivalent links in AppAppBar.
  const scrollTo = (id) => {
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${line}`, pt: { xs: 7, md: 9 }, pb: 4 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 5, md: 4 },
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1.4fr' },
          }}
        >
          {/* Brand */}
          <Box>
            <Logo size={30} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 340 }}>
              {t('footer.tagline')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('footer.since')}
            </Typography>
          </Box>

          {/* Site links */}
          <Box>
            <Typography variant="overline" component="p" sx={{ color: 'text.secondary', mb: 1.5 }}>
              {t('footer.company')}
            </Typography>
            <Stack spacing={1} alignItems="flex-start">
              {NAV_IDS.map((id) => (
                <Link
                  key={id}
                  component="button"
                  type="button"
                  onClick={() => scrollTo(id)}
                  sx={linkSx}
                >
                  {t(`nav.${id}`)}
                </Link>
              ))}
              <Link
                component="button"
                type="button"
                onClick={() => setLeadershipOpen((o) => !o)}
                aria-expanded={leadershipOpen}
                aria-controls={LEADERSHIP_ID}
                sx={{ ...linkSx, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {t('leadership.title')}
                <ExpandMoreRounded
                  sx={{
                    fontSize: 16,
                    transition: 'transform 0.2s',
                    transform: leadershipOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Link>
            </Stack>
          </Box>

          {/* Contact details */}
          <Box>
            <Typography variant="overline" component="p" sx={{ color: 'text.secondary', mb: 1.5 }}>
              {t('footer.contactTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('contact.address')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('footer.reg')}
            </Typography>
          </Box>
        </Box>

        <Leadership open={leadershipOpen} />

        {/* Bottom bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mt: { xs: 5, md: 7 }, pt: 3, borderTop: `1px solid ${line}` }}
        >
          <Typography variant="body2" color="text.secondary">
            {'© '}
            {new Date().getFullYear()}{' '}
            <Link href="https://www.dynapage.co.uk/" sx={{ color: 'text.primary' }}>
              DynaCloud Ltd
            </Link>
            {'. '}
            {t('footer.rights')}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              href="https://github.com/dynapage"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <GitHubIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
